import React, { forwardRef, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import mergeRefs from 'react-merge-refs';
import { useSwipeable } from 'react-swipeable';
import { CSSTransition } from 'react-transition-group';
import { Button } from '@alpha-recycling/component-library';
import styled from '@emotion/styled';

import { AlphamartHistoryShape, useAlphamartNavigate } from 'shared/hooks/useAlphamartRouter';
import { ItemAction } from 'shared/types';
import { sleep } from 'shared/utils/sleep';
import { layers, MEDIA_QUERY, NAVBAR_HEIGHT_MOBILE, Theme } from 'theme';
import { ActionsButton } from '../Buttons/ActionsButton';

type Options = {
  direction?: 'left' | 'right';
  width?: string;
  offset?: string;
  removePadding?: boolean;
  header?: {
    padding: 'M' | 'L';
  };
};

export interface DetailsField {
  label: string;
  value: ReactNode;
  name: string;
}

type OnCloseProps =
  | {
      onClose: () => void;
    }
  | { backUrl: string; backState?: AlphamartHistoryShape };

type DetailsProps = {
  open?: boolean;
  children: ReactNode;
  title: ReactNode;
  scrollable?: boolean;
  actions?: ItemAction[];
  onBeforeClose?: (onClose: () => void) => void | Promise<void>;
  swipeToClose?: boolean;
  options?: Options;
  className?: string;
} & OnCloseProps;

const DetailsOverlay = styled.div<{ theme?: Theme; options?: Options }>`
  position: fixed;
  top: 0;
  ${({ options }) => options?.direction ?? 'right'}: ${({ options }) => options?.offset ?? '0px'};
  width: 100vw;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  z-index: ${layers.details};

  @media ${MEDIA_QUERY.MAX_MD} {
    margin-top: ${NAVBAR_HEIGHT_MOBILE};
    ${({ options }) => options?.direction ?? 'right'}: 0px;
  }
`;

const DetailsLayout = styled.div<{ theme?: Theme; scrollable: boolean; options?: Options }>`
  background: ${({ theme }) => theme.pageColor};
  position: absolute;
  top: 0;
  bottom: 0;
  ${({ options }) => options?.direction ?? 'right'}: 0px;
  overflow-y: ${({ scrollable }) => (scrollable ? 'auto' : 'hidden')};
  overflow-x: hidden;
  transition: transform 0.2s ease;
  z-index: ${layers.details};

  width: 100%;
  @media ${MEDIA_QUERY.MD} {
    width: 65vw;
  }

  ${({ options }) => options?.width && `width: ${options.width};`}

  &.slide-enter {
    ${({ options }) => (options?.direction === 'left' ? 'left: -100%;' : 'right: -100%;')};
    transform: translateX(${({ options }) => (options?.direction === 'left' ? '-' : '')}100%);
  }

  &.slide-enter-active {
    transform: translateX(${({ options }) => (options?.direction === 'left' ? '-' : '')}100%);
  }

  &.slide-exit {
    transform: translateX(0);
  }

  &.slide-exit-active {
    transform: translateX(${({ options }) => (options?.direction === 'left' ? '-' : '')}100%);
  }
`;

const HeaderLayout = styled.header<{ theme?: Theme; options?: Options }>`
  display: flex;
  align-items: center;
  padding: 30px ${({ options }) => (options?.header?.padding === 'M' ? '36' : '102')}px;
  min-height: 100px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.mineShaftLightest};
  color: ${({ theme }) => theme.fontColor};
  font-size: 18px;
  word-break: break-word;
  justify-content: space-between;

  & > div {
    display: flex;
    align-items: center;

    & > button {
      margin-left: 16px;
    }
  }

  @media ${MEDIA_QUERY.MAX_XL} {
    padding-right: 56px;
    padding-left: 56px;
  }

  @media ${MEDIA_QUERY.MAX_MD} {
    align-items: flex-start;
    padding: 0px 24px 24px 24px;
    flex-direction: column-reverse;

    & > button {
      align-self: flex-end;
    }

    & > div {
      flex-direction: column;
      width: 100%;
      align-items: flex-start;

      & > button {
        margin-left: 0;
        margin-top: 8px;
      }

      & > div {
        width: 100%;
        & > button {
          width: 100%;
        }
      }
    }
  }
`;

const ContentLayout = styled.div<{ sectionsCount: number; options?: Options }>`
  height: fit-content;
  display: flex;
  gap: 16px;
  flex-direction: column;
  position: absolute;
  padding: ${({ options }) => (options?.removePadding ? '0px' : '16px 104px')};
  width: ${({ options }) => options?.width ?? '65vw'};

  @media ${MEDIA_QUERY.MAX_MD} {
    width: 100%;
  }

  @media ${MEDIA_QUERY.MAX_XL} {
    padding: ${({ options }) => (options?.removePadding ? '0px' : '0 51px')};
  }

  @media ${MEDIA_QUERY.MAX_MD} {
    min-width: 100%;
    max-width: 100%;
    width: 100vw;
    padding: ${({ options }) => (options?.removePadding ? '0px' : '8px 24px 0 24px')};
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  gap: 32px;
`;

export const Details = forwardRef<HTMLDivElement, DetailsProps>(
  (
    {
      open = true,
      title,
      children,
      scrollable = true,
      actions = [],
      onBeforeClose,
      swipeToClose = true,
      options,
      className,
      ...props
    }: DetailsProps,
    ref,
  ): React.ReactElement => {
    const [isMounted, setMounted] = useState(false);
    const navigate = useAlphamartNavigate();

    const onClose = async (): Promise<void> => {
      setMounted(false);
      await sleep(200);

      if ('onClose' in props) return props.onClose();
      return navigate(props.backUrl, { state: props.backState });
    };

    const onCloseAttempt = async (): Promise<void> => {
      if (!onBeforeClose) return onClose();

      return onBeforeClose(onClose);
    };

    const childrenFiltered = React.Children.toArray(children).filter(Boolean).length;

    const swipeHandler = useCallback(
      async ({ event }) => {
        const ids = ['gallery', 'imageLightbox', 'profileProfitMarginSlider'];
        const [galleryCoords, lightbox, pmSliderCoords] = ids.map(id =>
          document.querySelector(`#${id}`)?.getBoundingClientRect(),
        );
        const touch =
          event.type === 'touchend'
            ? event.touches[0] || event.changedTouches[0]
            : { clientX: event.clientX, clientY: event.clientY };
        const mouseCoords = {
          x: touch.clientX,
          y: touch.clientY,
        };
        const isTouchDevice = 'ontouchstart' in window;
        const isSwipingDisabled = [galleryCoords, lightbox, pmSliderCoords].some(
          el =>
            el &&
            mouseCoords?.x >= el.left &&
            mouseCoords?.x <= el.right &&
            mouseCoords?.y >= el.top &&
            mouseCoords?.y <= el.bottom,
        );

        if (isSwipingDisabled || !isTouchDevice || !swipeToClose) return;

        if (isMounted) {
          await onClose();
        }
      },
      [onClose, isMounted, swipeToClose],
    );

    const handlers = useSwipeable({
      trackTouch: true,
      trackMouse: true,
      onSwipedRight: swipeHandler,
    });

    useEffect(() => {
      setMounted(true);

      return () => {
        setMounted(false);
      };
    }, []);

    useEffect(() => {
      if (!open) {
        onClose();
      }
    }, [open]);

    const nodeRef = useRef<HTMLDivElement>(null);

    return (
      <DetailsOverlay
        onClick={onCloseAttempt}
        ref={ref}
        options={options}
        data-cy="details-overlay"
        className={className}
      >
        <CSSTransition in={isMounted} timeout={200} classNames="slide" nodeRef={nodeRef}>
          <DetailsLayout
            scrollable={scrollable}
            onClick={e => e.stopPropagation()}
            options={options}
            data-cy="details-layout"
            {...handlers}
            ref={mergeRefs([nodeRef, handlers.ref])}
          >
            <HeaderLayout options={options}>
              <HeaderWrapper>
                <div data-cy="header-name">{title}</div>
                <ActionsButton actions={actions} />
              </HeaderWrapper>
              <Button
                variant="transparent"
                content="icon"
                size="big"
                iconName="X"
                onClick={onCloseAttempt}
                data-cy="close-button"
              />
            </HeaderLayout>
            <ContentLayout sectionsCount={childrenFiltered} data-cy="section" options={options}>
              {children}
            </ContentLayout>
          </DetailsLayout>
        </CSSTransition>
      </DetailsOverlay>
    );
  },
);
